import React from "react";
import { CalendarX } from "lucide-react";

import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";

// A ÚNICA representação do ciclo da venda, a mesma em todas as telas:
//
//   Rascunho → Enviado → Aceito → Pedido → Pago → Entregue
//
// `separating`/`shipped` não são passos — aparecem como legenda sob "Pago"
// ("Pago · Separando", "Pago · Despachado"): oito pontos não cabem em 360px.
// Ramos terminais (recusado, vencido, cancelado) também não: a linha fica
// esmaecida até o último passo real e um Badge fecha a história.

const PASSOS = ["Rascunho", "Enviado", "Aceito", "Pedido", "Pago", "Entregue"];

// Cor do rótulo do passo atual na variante compacta — a mesma linguagem dos
// catálogos de status das telas.
const CLASSE_DO_PASSO = [
	"text-muted-foreground",
	"text-primary",
	"text-emerald-600",
	"text-amber-600",
	"text-emerald-600",
	"text-emerald-600",
];

// Onde o ciclo está, a partir do orçamento e (se houver) do pedido.
const ondeEsta = (situacao, statusPedido) => {
	if (statusPedido) {
		if (statusPedido === "cancelled")
			return { atual: 3, terminal: { rotulo: "Cancelado", destrutivo: true } };
		if (statusPedido === "pending_payment")
			return { atual: 3, legenda: "A pagar" };
		if (statusPedido === "delivered") return { atual: 5 };
		// paid, separating, shipped: o dinheiro entrou; o resto é legenda.
		return {
			atual: 4,
			legenda:
				statusPedido === "separating"
					? "Separando"
					: statusPedido === "shipped"
						? "Despachado"
						: null,
		};
	}

	if (situacao === "rejected")
		return { atual: 1, terminal: { rotulo: "Recusado", destrutivo: true } };
	if (situacao === "expired")
		return { atual: 1, terminal: { rotulo: "Vencido", vencido: true } };
	if (situacao === "accepted") return { atual: 2 };
	if (situacao === "sent") return { atual: 1 };
	if (situacao === "viewed") return { atual: 1, legenda: "Aberto pelo cliente" };
	return { atual: 0 };
};

const Ponto = ({ estado, destrutivo }) => (
	<span
		className={cn(
			"h-2.5 w-2.5 shrink-0 rounded-full",
			estado === "feito" && "bg-emerald-600",
			estado === "atual" &&
				(destrutivo
					? "bg-destructive ring-4 ring-background"
					: "bg-primary ring-4 ring-background"),
			estado === "futuro" && "border border-border bg-muted",
		)}
	/>
);

const BadgeTerminal = ({ terminal }) =>
	terminal.destrutivo ? (
		<Badge variant="destructive" className="whitespace-nowrap">
			{terminal.rotulo}
		</Badge>
	) : (
		<Badge variant="outline" className="gap-1 whitespace-nowrap text-muted-foreground">
			<CalendarX className="h-3 w-3" />
			{terminal.rotulo}
		</Badge>
	);

// O aceite sempre vira pedido (venda única): a história é sempre a completa.
const LinhaDoTempoDaVenda = ({ situacao, pedido, compacto, className }) => {
	const statusPedido = typeof pedido === "string" ? pedido : pedido?.status || null;
	const passos = PASSOS;

	const { atual, legenda, terminal } = ondeEsta(situacao, statusPedido);

	const rotuloAtual = `${passos[Math.min(atual, passos.length - 1)]}${
		legenda ? ` · ${legenda}` : ""
	}`;

	if (compacto) {
		return (
			<div className={cn("flex min-w-0 items-center gap-2", className)}>
				<div
					className={cn(
						"flex min-w-0 flex-1 items-center",
						terminal && "opacity-60",
					)}
				>
					{passos.map((passo, i) => (
						<React.Fragment key={passo}>
							{i > 0 && (
								<span
									className={cn(
										"h-px min-w-2 flex-1",
										i <= atual ? "bg-emerald-600" : "bg-border",
									)}
								/>
							)}
							<Ponto
								estado={i < atual ? "feito" : i === atual ? "atual" : "futuro"}
								destrutivo={i === atual && terminal?.destrutivo}
							/>
						</React.Fragment>
					))}
				</div>
				{terminal ? (
					<BadgeTerminal terminal={terminal} />
				) : (
					<span
						className={cn(
							"shrink-0 text-[11px]",
							CLASSE_DO_PASSO[atual] || "text-muted-foreground",
						)}
					>
						{rotuloAtual}
					</span>
				)}
			</div>
		);
	}

	return (
		<div className={cn("flex items-center gap-3", className)}>
			<div className={cn("flex flex-1 items-start", terminal && "opacity-60")}>
				{passos.map((passo, i) => (
					<React.Fragment key={passo}>
						{i > 0 && (
							<span
								className={cn(
									"mt-[5px] h-px min-w-3 flex-1",
									i <= atual ? "bg-emerald-600" : "bg-border",
								)}
							/>
						)}
						<span className="flex flex-col items-center gap-1 px-1">
							<Ponto
								estado={i < atual ? "feito" : i === atual ? "atual" : "futuro"}
								destrutivo={i === atual && terminal?.destrutivo}
							/>
							<span
								className={cn(
									"text-[11px] leading-tight",
									i === atual
										? "font-medium text-foreground"
										: "text-muted-foreground",
								)}
							>
								{i === atual && legenda ? rotuloAtual : passo}
							</span>
						</span>
					</React.Fragment>
				))}
			</div>
			{terminal && <BadgeTerminal terminal={terminal} />}
		</div>
	);
};

export default LinhaDoTempoDaVenda;
